import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import AlertCenter from "../components/AlertCenter";
import { API_BASE, ORBIS_BASE_PATH } from "../config/orbisConfig";

function authHeaders() {
    const token =
        localStorage.getItem("cani_auth") ||
        localStorage.getItem("token") ||
        localStorage.getItem("auth_token") ||
        localStorage.getItem("authToken") ||
        "";

    return {
        ...(token ? { Authorization: `Bearer ${token}`, "X-CANI-AUTH": token } : {}),
    };
}


function normalizeAssetPath(path) {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/orbispmo/")) return raw;
    if (raw.startsWith("/PM/")) return `/orbispmo/${raw.slice(4).replace(/^\/+/, "")}`;
    if (raw.startsWith("/")) return raw;
    return `/orbispmo/${raw.replace(/^\/+/, "")}`;
}

function normalizeAssetPathIfNeeded(key, value) {
    if (["logo_path", "logo_dark_path", "favicon_path", "login_background_path", "apple_touch_icon_path"].includes(key)) {
        return normalizeAssetPath(value);
    }
    return value ?? "";
}

function settingsValues(settings = {}) {
    const out = {};

    if (Array.isArray(settings)) {
        settings.forEach((row) => {
            const key = row?.setting_key || row?.key;
            if (key) {
                out[key] = normalizeAssetPathIfNeeded(key, row?.setting_value ?? row?.value ?? "");
            }
        });
        return out;
    }

    Object.entries(settings || {}).forEach(([key, row]) => {
        if (row && typeof row === "object" && !Array.isArray(row)) {
            out[key] = normalizeAssetPathIfNeeded(key, row?.setting_value ?? row?.value ?? "");
        } else {
            out[key] = normalizeAssetPathIfNeeded(key, row ?? "");
        }
    });

    return out;
}

function setDynamicFavicon(path) {
    if (!path) return;

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }

    link.href = normalizeAssetPath(path);
}

function readStoredPermissions() {
    try {
        const raw =
            localStorage.getItem("cani_permissions") ||
            localStorage.getItem("permissions") ||
            "[]";

        const parsed = JSON.parse(raw);

        if (Array.isArray(parsed)) return parsed;

        if (parsed && typeof parsed === "object") {
            return Object.entries(parsed)
                .filter(([, v]) => Number(v) === 1)
                .map(([k]) => k);
        }

        return [];
    } catch {
        return [];
    }
}

function getInitials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "U";
}

function roleLabel(role = "pm") {
    const map = {
        admin: "Administrador PMO",
        pm: "Project Manager",
        sponsor: "Sponsor",
        reviewer: "Revisor",
        stakeholder: "Stakeholder",
        responsible: "Responsable",
    };

    return map[role] || "Usuario PM";
}

function MenuBadge({ value }) {
    const num = Number(value || 0);

    if (num <= 0) return null;

    let bg = "#16a34a";
    if (num > 3 && num <= 7) bg = "#f59e0b";
    if (num > 7) bg = "#dc2626";

    return (
        <span
            style={{
                minWidth: 22,
                height: 22,
                padding: "0 7px",
                borderRadius: 999,
                background: bg,
                color: "#fff",
                fontSize: 11,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 8,
                boxShadow: "0 8px 18px rgba(15,23,42,.18)",
            }}
        >
            {num}
        </span>
    );
}

function SoonBadge() {
    return (
        <span
            style={{
                fontSize: 9,
                fontWeight: 800,
                padding: "3px 7px",
                borderRadius: 999,
                background: "rgba(148,163,184,.18)",
                color: "#94A3B8",
                letterSpacing: ".02em",
                whiteSpace: "nowrap",
            }}
        >
            Próx.
        </span>
    );
}

function UserAvatar({ name, src, size = 38 }) {
    const initials = getInitials(name);

    if (src) {
        return (
            <img
                src={src}
                alt={name}
                style={{
                    width: size,
                    height: size,
                    borderRadius: 999,
                    objectFit: "cover",
                    border: "2px solid rgba(96,165,250,.65)",
                    boxShadow: "0 10px 24px rgba(15,23,42,.22)",
                }}
            />
        );
    }

    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                    "linear-gradient(135deg, rgba(37,99,235,1), rgba(124,58,237,1))",
                color: "#fff",
                fontWeight: 900,
                fontSize: size <= 32 ? 11 : 13,
                border: "2px solid rgba(255,255,255,.28)",
                boxShadow: "0 10px 24px rgba(15,23,42,.22)",
            }}
        >
            {initials}
        </div>
    );
}

export default function MainLayout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [permissions, setPermissions] = useState(readStoredPermissions());
    const [myWorkBadge, setMyWorkBadge] = useState(0);
    const [projectsBadge, setProjectsBadge] = useState(0);
    const [platformSettings, setPlatformSettings] = useState({});
    const [platformUser, setPlatformUser] = useState(null);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        return localStorage.getItem("cani_sidebar_collapsed") === "1";
    });

    const cfg = settingsValues(platformSettings);

    const displayName =
        platformUser?.display_name ||
        platformUser?.name ||
        platformUser?.full_name ||
        localStorage.getItem("displayName") ||
        "Usuario";

    const role =
        platformUser?.role ||
        localStorage.getItem("role") ||
        "pm";

    const profilePhoto =
        platformUser?.avatar_path ||
        localStorage.getItem("profile_photo_url") ||
        localStorage.getItem("photo_url") ||
        localStorage.getItem("avatar_url") ||
        "";

    const primaryColor = cfg.primary_color || "#041c2c";
    const secondaryColor = cfg.secondary_color || "#0f172a";
    const accentColor = cfg.accent_color || "#f4b91b";
    const platformName = cfg.platform_name || "CLIP · ORBIS PMO";
    const platformShortName = cfg.platform_short_name || "CLIP ORBIS";
    const commercialName = cfg.commercial_name || "ORBIS PMO";
    const sidebarLogo = cfg.logo_dark_path || cfg.logo_path || "";
    const compactLogo = cfg.favicon_path || cfg.logo_path || "";

    const loadPlatformSettings = async () => {
        try {
            const response = await fetch(`${API_BASE}/platform_settings_get.php`, {
                headers: authHeaders(),
            });

            const text = await response.text();
            if (!text || !text.trim()) return;

            const data = JSON.parse(text);
            if (!response.ok || data?.ok === false || data?.success === false) return;

            setPlatformSettings(data.values || data.settings || {});
            setPlatformUser(data.user || null);

            const values = settingsValues(data.values || data.settings || {});

            if (values.favicon_path) setDynamicFavicon(values.favicon_path);

            if (data.user?.display_name || data.user?.name || data.user?.full_name) {
                localStorage.setItem("displayName", data.user.display_name || data.user.name || data.user.full_name);
            }

            if (data.user?.avatar_path) {
                localStorage.setItem("profile_photo_url", data.user.avatar_path);
            }
        } catch (err) {
            console.warn("platform_settings_get error:", err);
        }
    };

    useEffect(() => {
        loadPlatformSettings();

        window.addEventListener("platform-settings-updated", loadPlatformSettings);

        return () => {
            window.removeEventListener("platform-settings-updated", loadPlatformSettings);
        };
    }, []);

    useEffect(() => {
        loadMyWorkBadge();
        loadProjectsBadge();

        const interval = setInterval(() => {
            loadMyWorkBadge();
            loadProjectsBadge();
        }, 30000);

        return () => clearInterval(interval);
    }, [location.pathname]);

    const loadEffectivePermissions = async () => {
        try {
            const response = await fetch(`${API_BASE}/permissions_my_permissions.php`, {
                headers: authHeaders(),
            });

            const txt = await response.text();
            if (!txt || !txt.trim()) return;

            const data = JSON.parse(txt);
            if (!response.ok || data?.ok === false || data?.success === false) return;

            const list = Array.isArray(data.permissions) ? data.permissions : [];
            localStorage.setItem("cani_permissions", JSON.stringify(list));
            setPermissions(list);
        } catch (err) {
            console.warn("permissions_my_permissions error:", err);
        }
    };

    useEffect(() => {
        const refreshPerms = () => setPermissions(readStoredPermissions());

        window.addEventListener("storage", refreshPerms);
        window.addEventListener("cani-permissions-updated", refreshPerms);

        return () => {
            window.removeEventListener("storage", refreshPerms);
            window.removeEventListener("cani-permissions-updated", refreshPerms);
        };
    }, []);

    useEffect(() => {
        loadEffectivePermissions();
    }, [role]);

    const can = (key) => {
        if (!key) return true;
        if (role === "admin") return true;
        return permissions.includes(key);
    };

    const hasAny = (keys = []) => {
        if (role === "admin") return true;
        return keys.some((k) => permissions.includes(k));
    };

    const loadMyWorkBadge = async () => {
        try {
            const loggedUserId =
                Number(localStorage.getItem("user_id")) ||
                Number(localStorage.getItem("id")) ||
                1;

            const token =
                localStorage.getItem("cani_auth") ||
                localStorage.getItem("token") ||
                "";

            const response = await fetch(`/orbispmo/api/my_tasks.php?user_id=${loggedUserId}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const txt = await response.text();

            if (!txt || !txt.trim()) {
                setMyWorkBadge(0);
                return;
            }

            const result = JSON.parse(txt);

            if (!response.ok || !result.success) {
                setMyWorkBadge(0);
                return;
            }

            const tasks = Array.isArray(result.data) ? result.data : [];

            const urgentCount = tasks.filter((task) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const overdue =
                    task.due_date &&
                    task.status !== "terminada" &&
                    new Date(task.due_date).setHours(0, 0, 0, 0) < today.getTime();

                const inReview =
                    task.review_status === "pendiente" ||
                    task.review_status === "en_revision";

                const critical =
                    (task.priority || "").toLowerCase() === "critica" &&
                    task.status !== "terminada";

                const blocked = Number(task.is_blocked) === 1;

                const noEvidence =
                    Number(task.evidence_required || 0) === 1 &&
                    Number(task.evidence_total || 0) === 0;

                return overdue || inReview || critical || blocked || noEvidence;
            }).length;

            setMyWorkBadge(urgentCount);
        } catch {
            setMyWorkBadge(0);
        }
    };

    const loadProjectsBadge = async () => {
        try {
            const token =
                localStorage.getItem("cani_auth") ||
                localStorage.getItem("token") ||
                "";

            const headers = {
                Authorization: token ? `Bearer ${token}` : "",
                "X-CANI-AUTH": token || "",
            };

            const endpoints = [
                `${API_BASE}/projects_list.php`,
                `${API_BASE}/projects.php`,
            ];

            let badgeCount = 0;

            for (const url of endpoints) {
                try {
                    const response = await fetch(url, { headers });
                    const txt = await response.text();

                    if (!txt || !txt.trim()) continue;

                    const result = JSON.parse(txt);

                    const list =
                        Array.isArray(result?.data) ? result.data :
                            Array.isArray(result?.items) ? result.items :
                                Array.isArray(result?.rows) ? result.rows :
                                    Array.isArray(result) ? result :
                                        [];

                    if (!list.length) continue;

                    const activeProjects = list.filter((p) => {
                        const status = String(p?.status || "").toLowerCase();
                        return status !== "terminado" && status !== "cancelado";
                    });

                    badgeCount = activeProjects.length;
                    break;
                } catch (err) {
                    console.error("loadProjectsBadge endpoint error:", url, err);
                }
            }

            setProjectsBadge(Number(badgeCount) || 0);
        } catch (err) {
            console.error("loadProjectsBadge fatal:", err);
            setProjectsBadge(0);
        }
    };

    const menuSections = useMemo(() => {
        return [
            {
                id: "dashboard",
                title: "Command Center",
                icon: "◈",
                description: "Vista ejecutiva",
                show: hasAny(["dashboard.view", "dashboardexecutive.view", "projects.view"]),
                items: [
                    {
                        to: "/dashboardexecutive",
                        label: "Dashboard Ejecutivo",
                        perm: "dashboardexecutive.view",
                        icon: "▣",
                        help: "Command Center para salud ejecutiva, alertas, integridad PMO y métricas críticas.",
                    },
                    {
                        to: "/dashboard",
                        label: "Dashboard Operativo",
                        perm: "dashboard.view",
                        icon: "▤",
                        help: "Vista operativa para seguimiento diario de actividad, avances y operación general.",
                    },
                    {
                        to: "/dashboardexecutive",
                        label: "PMO Overview",
                        perm: "dashboardexecutive.view",
                        icon: "◇",
                        help: "Resumen ejecutivo consolidado del portafolio PMO.",
                        soon: true,
                    },
                ],
            },
            {
                id: "daily",
                title: "Trabajo Diario",
                icon: "◆",
                description: "Ejecución diaria",
                show: hasAny(["projects.view"]),
                items: [
                    {
                        to: "/my-work",
                        label: "Mi Trabajo",
                        perm: "projects.view",
                        icon: "●",
                        help: "Carga operativa total del usuario: tareas como responsable y tareas donde participa como revisor.",
                        badgeKey: "myWork",
                    },
                    {
                        to: "/my-work",
                        label: "Mis Revisiones",
                        perm: "projects.view",
                        icon: "◐",
                        help: "Vista enfocada en decisiones de revisión: aprobar, rechazar o solicitar cambios.",
                        soon: true,
                    },
                    {
                        to: "/agenda",
                        label: "Agenda Operativa",
                        perm: "agenda.view",
                        icon: "◷",
                        help: "Agenda de actividades, reuniones, seguimientos y compromisos operativos.",
                    },
                    {
                        to: "/my-work",
                        label: "Evidencias",
                        perm: "projects.view",
                        icon: "◎",
                        help: "Repositorio operativo de evidencias asociadas a tareas y avances.",
                        soon: true,
                    },
                ],
            },
            {
                id: "projects",
                title: "Proyectos",
                icon: "▰",
                description: "Planeación y control",
                show: hasAny(["projects.view"]),
                items: [
                    {
                        to: "/projects",
                        label: "Todos los Proyectos",
                        perm: "projects.view",
                        icon: "▦",
                        help: "Listado general de proyectos, estados, responsables y acceso a Ficha 360.",
                        badgeKey: "projects",
                    },
                    {
                        to: "/project-templates",
                        label: "Plantillas",
                        perm: "projects.view",
                        icon: "▧",
                        help: "Biblioteca de plantillas para crear proyectos con estructura base y WBS.",
                    },
                    {
                        to: "/projects",
                        label: "Portafolio",
                        perm: "projects.view",
                        icon: "▥",
                        help: "Vista consolidada del portafolio de proyectos, salud y prioridad ejecutiva.",
                        soon: true,
                    },
                    {
                        to: "/roadmap",
                        label: "Roadmap",
                        perm: "projects.view",
                        icon: "↗",
                        help: "Mapa de ruta ejecutivo por fases, avance, vencimientos y salud del proyecto.",
                    }, {
                        to: "/gantt",
                        label: "Gantt",
                        perm: "projects.view",
                        icon: "▭",
                        help: "Vista temporal de tareas, fechas, dependencias y ruta de ejecución.",
                    }, {
                        to: "/kanban",
                        label: "Kanban",
                        perm: "projects.view",
                        icon: "▥",
                        help: "Flujo operativo por estado, revisión, responsable, vencimiento e integridad PMO.",
                    },
                ],
            },
            {
                id: "pmo",
                title: "PMO",
                icon: "✦",
                description: "Gobierno y métricas",
                show: hasAny(["projects.view"]),
                items: [
                    {
                        to: "/dashboardexecutive",
                        label: "Métricas PMO",
                        perm: "dashboardexecutive.view",
                        icon: "◍",
                        help: "Vista ejecutiva de salud, readiness, vencimientos, integridad operativa y alertas PMO.",
                    },
                    {
                        to: "/decision-center",
                        label: "Centro de Decisiones",
                        perm: "projects.view",
                        icon: "◆",
                        help: "Bandeja ejecutiva de acciones urgentes: vencidas, críticas, sin revisor, sin evidencia o con avance inconsistente.",
                    },
                    {
                        to: "/productivity",
                        label: "Productividad PMO",
                        perm: "projects.view",
                        icon: "◍",
                        help: "Mide confiabilidad por responsable, presión de revisión y cuellos de botella operativos.",
                    },
                    {
                        to: "/evidence-map",
                        label: "Mapa de Evidencias",
                        perm: "projects.view",
                        icon: "⌖",
                        help: "Visualiza evidencias georreferenciadas por proyecto, tarea, responsable y ubicación.",
                    },
                    {
                        to: "/projects",
                        label: "Riesgos",
                        perm: "projects.view",
                        icon: "▲",
                        help: "Consulta riesgos del proyecto, exposición operativa, impacto y probabilidad.",
                        soon: true,
                    },
                    {
                        to: "/projects",
                        label: "Entregables",
                        perm: "projects.view",
                        icon: "■",
                        help: "Da seguimiento a compromisos, entregables aprobados, pendientes y vencidos.",
                        soon: true,
                    },
                    {
                        to: "/projects",
                        label: "Stakeholders",
                        perm: "projects.view",
                        icon: "◌",
                        help: "Identifica actores clave, influencia, interés y estrategia de comunicación.",
                        soon: true,
                    },
                    {
                        to: "/dashboardexecutive",
                        label: "Readiness Meter",
                        perm: "dashboardexecutive.view",
                        icon: "◉",
                        help: "Compara avance real contra avance esperado para detectar riesgo de incumplimiento.",
                        soon: true,
                    },
                    {
                        to: "/dashboardexecutive",
                        label: "Aging & Bloqueos",
                        perm: "dashboardexecutive.view",
                        icon: "⏱",
                        help: "Muestra antigüedad de tareas vencidas, bloqueos y deuda operativa acumulada.",
                        soon: true,
                    },
                ],
            },
            {
                id: "institution",
                title: "Institución",
                icon: "⬟",
                description: "CLIP y ecosistema",
                show: hasAny([
                    "institution.view",
                    "affiliate_companies.view",
                    "affiliations.view",
                    "contacts.view",
                    "directory.view",
                ]),
                items: [
                    {
                        to: "/institution",
                        label: "Datos CLIP",
                        perm: "institution.view",
                        icon: "◆",
                        help: "Información institucional base de Consejo Lagunero de la Iniciativa Privada.",
                    },
                    {
                        to: "/affiliate-companies",
                        label: "Cámaras y Organismos",
                        perm: "affiliate_companies.view",
                        icon: "◧",
                        help: "Gestión de Cámras y Organismos empresariales prospecto para afiliación y vinculación institucional.",
                    },
                    //{
                    //    to: "/affiliations",
                    //    label: "Procesos de Afiliación",
                    //    perm: "affiliations.view",
                    //    icon: "◨",
                    //    help: "Seguimiento de procesos para convertir prospectos en afiliados.",
                    //},
                    {
                        to: "/contacts",
                        label: "Contactos",
                        perm: "contacts.view",
                        icon: "◌",
                        help: "Base de contactos relacionada con cuentas, instituciones y oportunidades.",
                    },
                    {
                        to: "/directory",
                        label: "Directorio Estratégico",
                        perm: "directory.view",
                        icon: "◎",
                        help: "Directorio de actores estratégicos del ecosistema empresarial, gobierno y academia.",
                    },
                ],
            },
            {
                id: "crm",
                title: "CRM Comercial",
                icon: "◖",
                description: "Ventas y pipeline",
                show: hasAny([
                    "accounts.view",
                    "opportunities.view",
                    "agenda.view",
                    "contacts.view",
                ]),
                items: [
                    {
                        to: "/crm-dashboard",
                        label: "Dashboard Comercial",
                        perm: "opportunities.view",
                        icon: "◈",
                        help: "Centro de mando comercial: pipeline, campañas, agenda, valor piloto, potencial mensual y oportunidades sin próxima acción.",
                    },
                    {
                        to: "/crm-quality",
                        label: "Calidad Comercial",
                        perm: "opportunities.view",
                        icon: "◉",
                        help: "Score de calidad CRM: contacto clave, próxima acción, valor comercial, actividad vinculada y faltantes por oportunidad.",
                    },
                    {
                        to: "/orbis-demo-requests",
                        label: "Leads Web ORBIS",
                        perm: "opportunities.view",
                        icon: "◐",
                        help: "Solicitudes recibidas desde la landing de ORBIS PMO para convertirlas en cuenta, contacto, oportunidad y actividad comercial.",
                    },
                    {
                        to: "/opportunities",
                        label: "Oportunidades",
                        perm: "opportunities.view",
                        icon: "✧",
                        help: "Ficha Comercial 360 de oportunidades: pipeline, contacto clave, próxima acción, actividades y valor comercial.",
                    },
                    {
                        to: "/agenda",
                        label: "Agenda Comercial",
                        perm: "agenda.view",
                        icon: "◷",
                        help: "Seguimientos comerciales, reuniones, demos, propuestas, actividades vencidas y próximas acciones.",
                    },
                    {
                        to: "/accounts",
                        label: "Cuentas",
                        perm: "accounts.view",
                        icon: "▣",
                        help: "Gestión de cuentas estratégicas, empresas, instituciones y relaciones clave.",
                    },
                    {
                        to: "/contacts",
                        label: "Contactos",
                        perm: "contacts.view",
                        icon: "◌",
                        help: "Base de contactos relacionada con cuentas, oportunidades y actividad comercial.",
                    },
                ],
            },
            {
                id: "admin",
                title: "Administración",
                icon: "⚙",
                description: "Usuarios y permisos",
                show: hasAny(["users.manage"]) || role === "admin",
                items: [
                    {
                        to: "/users",
                        label: "Usuarios",
                        perm: "users.manage",
                        icon: "●",
                        help: "Administración de usuarios, roles, permisos y accesos a la plataforma.",
                    },
                    {
                        to: "/permissions",
                        label: "Permisos",
                        perm: "permissions.manage",
                        icon: "◆",
                        help: "Matriz de permisos por rol, usuario, módulo y acceso específico.",
                    },
                    {
                        to: "/platform-settings",
                        label: "Configuración",
                        perm: "platform_settings.view",
                        icon: "⚙",
                        help: "Logo, favicon, dominio, colores, identidad y perfil de usuario.",
                    },
                ],
            },
        ];
    }, [permissions, role]);

    const visibleSections = menuSections
        .filter((section) => section.show)
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => can(item.perm)),
        }))
        .filter((section) => section.items.length > 0);

    const getSectionActive = (section) => {
        return section.items.some((item) => {
            if (item.to === "/projects" && location.pathname.startsWith("/projects")) return true;
            if (item.to === "/accounts" && location.pathname.startsWith("/accounts")) return true;
            if (item.to === "/opportunities" && location.pathname.startsWith("/opportunities")) return true;
            if (item.to === "/crm-dashboard" && location.pathname.startsWith("/crm-dashboard")) return true;
            if (item.to === "/crm-quality" && location.pathname.startsWith("/crm-quality")) return true;
            if (item.to === "/contacts" && location.pathname.startsWith("/contacts")) return true;
            if (item.to === "/crm-dashboard" && location.pathname.startsWith("/crm-dashboard")) return true;
            if (item.to === "/crm-quality" && location.pathname.startsWith("/crm-quality")) return true;
            if (item.to === "/directory" && location.pathname.startsWith("/directory")) return true;
            if (item.to === "/my-work" && location.pathname.startsWith("/my-work")) return true;
            if (item.to === "/evidence-map" && location.pathname.startsWith("/evidence-map")) return true;
            if (item.to === "/productivity" && location.pathname.startsWith("/productivity")) return true;
            if (item.to === "/permissions" && location.pathname.startsWith("/permissions")) return true;
            if (item.to === "/platform-settings" && location.pathname.startsWith("/platform-settings")) return true;

            return location.pathname === item.to;
        });
    };

    const initialExpanded = useMemo(() => {
        const active = visibleSections.find((section) => getSectionActive(section));
        return active?.id || visibleSections[0]?.id || "dashboard";
    }, [location.pathname, visibleSections.length]);

    const [expandedSections, setExpandedSections] = useState(() => {
        try {
            const raw = localStorage.getItem("cani_sidebar_expanded");
            const parsed = raw ? JSON.parse(raw) : null;
            if (parsed && typeof parsed === "object") return parsed;
        } catch { }

        return {};
    });

    useEffect(() => {
        if (!initialExpanded) return;

        setExpandedSections((prev) => ({
            ...prev,
            [initialExpanded]: true,
        }));
    }, [initialExpanded]);

    const toggleSidebar = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem("cani_sidebar_collapsed", next ? "1" : "0");
            return next;
        });
    };

    const toggleSection = (sectionId) => {
        setExpandedSections((prev) => {
            const next = {
                ...prev,
                [sectionId]: !prev[sectionId],
            };

            localStorage.setItem("cani_sidebar_expanded", JSON.stringify(next));
            return next;
        });
    };

    const clearSession = () => {
        [
            "cani_auth",
            "displayName",
            "role",
            "user_id",
            "email",
            "permissions",
            "cani_permissions",
            "profile_photo_url",
            "photo_url",
            "avatar_url",
        ].forEach((k) => localStorage.removeItem(k));
    };

    const handleLogout = async () => {
        try {
            const token =
                localStorage.getItem("cani_auth") ||
                localStorage.getItem("token") ||
                "";

            if (token) {
                await fetch(`${API_BASE}/auth_logout.php`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "X-CANI-AUTH": token,
                    },
                });
            }
        } catch (err) {
            console.warn("logout error:", err);
        }

        // Limpieza completa de sesión, permisos y perfil
        localStorage.removeItem("cani_auth");
        localStorage.removeItem("token");
        localStorage.removeItem("cani_permissions");
        localStorage.removeItem("permissions");
        localStorage.removeItem("user_id");
        localStorage.removeItem("id");
        localStorage.removeItem("role");
        localStorage.removeItem("displayName");
        localStorage.removeItem("profile_photo_url");
        localStorage.removeItem("photo_url");
        localStorage.removeItem("avatar_url");

        // Si clearSession limpia otras llaves internas, lo dejamos también
        try {
            clearSession();
        } catch (err) {
            console.warn("clearSession error:", err);
        }

        // Avisar a la app que permisos/sesión cambiaron
        window.dispatchEvent(new Event("cani-permissions-updated"));

        // Redirección dura para reinicializar router, layout y permisos
        const loginPath = `${ORBIS_BASE_PATH || ""}/login`.replace(/\/+/g, "/");
        window.location.replace(loginPath || "/login");
    };
    const currentPageMeta = (() => {
        for (const section of visibleSections) {
            const found = section.items.find((item) => {
                if (item.to === "/projects" && location.pathname.startsWith("/projects")) return true;
                if (item.to === "/accounts" && location.pathname.startsWith("/accounts")) return true;
                if (item.to === "/contacts" && location.pathname.startsWith("/contacts")) return true;
                if (item.to === "/directory" && location.pathname.startsWith("/directory")) return true;
                if (item.to === "/my-work" && location.pathname.startsWith("/my-work")) return true;
                if (item.to === "/evidence-map" && location.pathname.startsWith("/evidence-map")) return true;
                if (item.to === "/productivity" && location.pathname.startsWith("/productivity")) return true;
                if (item.to === "/permissions" && location.pathname.startsWith("/permissions")) return true;
                if (item.to === "/platform-settings" && location.pathname.startsWith("/platform-settings")) return true;

                return location.pathname === item.to;
            });

            if (found) {
                return {
                    title: found.label,
                    subtitle: section.description,
                    section: section.title,
                };
            }
        }

        return {
            title: role === "admin" ? "Administrador" : "PM / Líder de proyectos",
            subtitle: platformName,
            section: platformShortName,
        };
    })();

    useEffect(() => {
        const page = currentPageMeta?.title || platformShortName;
        document.title = `${page} · ${commercialName || platformShortName}`;
        if (cfg.favicon_path) setDynamicFavicon(cfg.favicon_path);
    }, [currentPageMeta?.title, commercialName, platformShortName, cfg.favicon_path]);

    const renderBadge = (badgeKey) => {
        if (badgeKey === "myWork") return <MenuBadge value={myWorkBadge} />;
        if (badgeKey === "projects") return <MenuBadge value={projectsBadge} />;
        return null;
    };

    const sidebarWidth = sidebarCollapsed ? 84 : 292;

    return (
        <div
            className="min-h-screen flex text-slate-900 overflow-hidden"
            style={{
                background:
                    "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <aside
                className="hidden md:flex flex-col shrink-0"
                style={{
                    width: sidebarWidth,
                    background:
                        `linear-gradient(180deg, ${secondaryColor} 0%, #111827 48%, #020617 100%)`,
                    color: "#E5E7EB",
                    borderRight: "1px solid rgba(148,163,184,.18)",
                    boxShadow: "18px 0 55px rgba(15,23,42,.20)",
                    transition: "width .22s ease",
                }}
            >
                <div
                    className="flex items-center justify-between"
                    style={{
                        padding: sidebarCollapsed ? "16px 14px" : "18px 18px",
                        borderBottom: "1px solid rgba(148,163,184,.18)",
                    }}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 16,
                                background: "rgba(255,255,255,.08)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid rgba(255,255,255,.12)",
                                boxShadow: "0 14px 35px rgba(0,0,0,.25)",
                            }}
                        >
                            <img
                                src={normalizeAssetPath(compactLogo)}
                                alt={platformShortName}
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 10,
                                    objectFit: "contain",
                                }}
                            />
                        </div>

                        {!sidebarCollapsed ? (
                            <div className="min-w-0">
                                <img
                                    src={normalizeAssetPath(sidebarLogo)}
                                    alt={platformName}
                                    style={{
                                        height: 24,
                                        maxWidth: 145,
                                        objectFit: "contain",
                                        filter: "none",
                                    }}
                                />
                                <div
                                    style={{
                                        fontSize: 10,
                                        color: "#93C5FD",
                                        fontWeight: 800,
                                        letterSpacing: ".08em",
                                        textTransform: "uppercase",
                                        marginTop: 4,
                                    }}
                                >
                                    {commercialName}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <button
                        type="button"
                        onClick={toggleSidebar}
                        title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 999,
                            border: "1px solid rgba(148,163,184,.25)",
                            background: "rgba(255,255,255,.07)",
                            color: "#E5E7EB",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            cursor: "pointer",
                        }}
                    >
                        {sidebarCollapsed ? "›" : "‹"}
                    </button>
                </div>

                <nav
                    className="flex-1 overflow-y-auto"
                    style={{
                        padding: sidebarCollapsed ? "14px 10px" : "16px 14px",
                    }}
                >
                    {visibleSections.map((section) => {
                        const activeSection = getSectionActive(section);
                        const expanded = sidebarCollapsed ? false : expandedSections[section.id];

                        return (
                            <div key={section.id} style={{ marginBottom: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => toggleSection(section.id)}
                                    title={sidebarCollapsed ? section.title : ""}
                                    style={{
                                        width: "100%",
                                        border: 0,
                                        borderRadius: 16,
                                        padding: sidebarCollapsed ? "11px 0" : "11px 12px",
                                        background: activeSection
                                            ? "linear-gradient(135deg, rgba(37,99,235,.34), rgba(124,58,237,.18))"
                                            : "transparent",
                                        color: activeSection ? "#FFFFFF" : "#CBD5E1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: sidebarCollapsed ? "center" : "space-between",
                                        cursor: "pointer",
                                        transition: "all .18s ease",
                                        border: activeSection
                                            ? "1px solid rgba(96,165,250,.35)"
                                            : "1px solid transparent",
                                        boxShadow: activeSection
                                            ? "0 12px 28px rgba(37,99,235,.18)"
                                            : "none",
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            minWidth: 0,
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 12,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: activeSection
                                                    ? "rgba(255,255,255,.14)"
                                                    : "rgba(148,163,184,.10)",
                                                color: activeSection ? "#DBEAFE" : "#94A3B8",
                                                fontSize: 14,
                                                fontWeight: 900,
                                            }}
                                        >
                                            {section.icon}
                                        </span>

                                        {!sidebarCollapsed ? (
                                            <span className="min-w-0 text-left">
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        letterSpacing: ".01em",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {section.title}
                                                </span>
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: 10,
                                                        color: activeSection ? "#BFDBFE" : "#64748B",
                                                        marginTop: 1,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {section.description}
                                                </span>
                                            </span>
                                        ) : null}
                                    </span>

                                    {!sidebarCollapsed ? (
                                        <span
                                            style={{
                                                color: activeSection ? "#DBEAFE" : "#64748B",
                                                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                                                transition: "transform .16s ease",
                                                fontSize: 15,
                                            }}
                                        >
                                            ›
                                        </span>
                                    ) : null}
                                </button>

                                {!sidebarCollapsed && expanded ? (
                                    <ul style={{ marginTop: 7, marginLeft: 12, paddingLeft: 16, borderLeft: "1px solid rgba(148,163,184,.14)" }}>
                                        {section.items.map((item) => {
                                            const itemActive =
                                                (item.to === "/projects" && location.pathname.startsWith("/projects")) ||
                                                (item.to === "/accounts" && location.pathname.startsWith("/accounts")) ||
                                                (item.to === "/opportunities" && location.pathname.startsWith("/opportunities")) ||
                                                (item.to === "/crm-dashboard" && location.pathname.startsWith("/crm-dashboard")) ||
                                                (item.to === "/crm-quality" && location.pathname.startsWith("/crm-quality")) ||
                                                (item.to === "/contacts" && location.pathname.startsWith("/contacts")) ||
                                                (item.to === "/directory" && location.pathname.startsWith("/directory")) ||
                                                (item.to === "/my-work" && location.pathname.startsWith("/my-work")) ||
                                                (item.to === "/evidence-map" && location.pathname.startsWith("/evidence-map")) ||
                                                (item.to === "/productivity" && location.pathname.startsWith("/productivity")) ||
                                                location.pathname === item.to;

                                            const content = (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        width: "100%",
                                                        gap: 10,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 9,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                width: 22,
                                                                height: 22,
                                                                borderRadius: 9,
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                background: itemActive
                                                                    ? "rgba(37,99,235,.20)"
                                                                    : "rgba(148,163,184,.08)",
                                                                color: itemActive ? "#93C5FD" : "#64748B",
                                                                fontSize: 11,
                                                                fontWeight: 900,
                                                                flex: "0 0 auto",
                                                            }}
                                                        >
                                                            {item.icon}
                                                        </span>

                                                        <span
                                                            style={{
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {item.label}
                                                        </span>
                                                    </span>

                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                        }}
                                                    >
                                                        {item.soon ? <SoonBadge /> : null}
                                                        {renderBadge(item.badgeKey)}
                                                    </span>
                                                </span>
                                            );

                                            if (item.soon) {
                                                return (
                                                    <li key={`${section.id}-${item.label}`} style={{ listStyle: "none", marginBottom: 4 }}>
                                                        <div
                                                            title={item.help || "Módulo contemplado para siguientes fases"}
                                                            style={{
                                                                width: "100%",
                                                                borderRadius: 13,
                                                                padding: "9px 10px",
                                                                color: "#64748B",
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                opacity: .95,
                                                            }}
                                                        >
                                                            {content}
                                                        </div>
                                                    </li>
                                                );
                                            }

                                            return (
                                                <li key={`${section.id}-${item.to}-${item.label}`} style={{ listStyle: "none", marginBottom: 4 }}>
                                                    <NavLink
                                                        to={item.to}
                                                        title={item.help || item.label}
                                                        style={{
                                                            width: "100%",
                                                            borderRadius: 13,
                                                            padding: "9px 10px",
                                                            display: "flex",
                                                            color: itemActive ? "#FFFFFF" : "#CBD5E1",
                                                            background: itemActive
                                                                ? "rgba(37,99,235,.22)"
                                                                : "transparent",
                                                            fontSize: 12,
                                                            fontWeight: itemActive ? 900 : 700,
                                                            textDecoration: "none",
                                                            border: itemActive
                                                                ? "1px solid rgba(96,165,250,.22)"
                                                                : "1px solid transparent",
                                                        }}
                                                    >
                                                        {content}
                                                    </NavLink>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}
                            </div>
                        );
                    })}

                    {visibleSections.length === 0 ? (
                        <div
                            style={{
                                padding: 14,
                                borderRadius: 18,
                                background: "rgba(255,255,255,.06)",
                                border: "1px solid rgba(148,163,184,.18)",
                                color: "#94A3B8",
                                fontSize: 12,
                                lineHeight: 1.45,
                            }}
                        >
                            No hay módulos asignados para este usuario.
                        </div>
                    ) : null}
                </nav>

                <div
                    style={{
                        padding: sidebarCollapsed ? "14px 10px" : "14px 16px",
                        borderTop: "1px solid rgba(148,163,184,.18)",
                    }}
                >
                    <div
                        style={{
                            borderRadius: 18,
                            background: "rgba(255,255,255,.07)",
                            border: "1px solid rgba(148,163,184,.18)",
                            padding: sidebarCollapsed ? 8 : 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: sidebarCollapsed ? "center" : "space-between",
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                minWidth: 0,
                            }}
                        >
                            <UserAvatar name={displayName} src={profilePhoto} size={sidebarCollapsed ? 36 : 40} />

                            {!sidebarCollapsed ? (
                                <div className="min-w-0">
                                    <div
                                        style={{
                                            color: "#FFFFFF",
                                            fontSize: 12,
                                            fontWeight: 900,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: 150,
                                        }}
                                    >
                                        {displayName}
                                    </div>
                                    <div
                                        style={{
                                            color: "#93C5FD",
                                            fontSize: 10,
                                            fontWeight: 800,
                                            marginTop: 2,
                                        }}
                                    >
                                        {roleLabel(role)}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {!sidebarCollapsed ? (
                        <div
                            style={{
                                marginTop: 10,
                                color: "#64748B",
                                fontSize: 10,
                                lineHeight: 1.35,
                            }}
                        >
                            CANIETI Coahuila–Durango
                            <br />
                            Plataforma ORBIS PMO / Institucional
                        </div>
                    ) : null}
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header
                    className="shrink-0 flex items-center justify-between"
                    style={{
                        height: 72,
                        position: "relative",
                        zIndex: 99999,
                        background: "rgba(255,255,255,.86)",
                        backdropFilter: "blur(16px)",
                        borderBottom: "1px solid rgba(226,232,240,.88)",
                        padding: "0 24px",
                        boxShadow: "0 12px 38px rgba(15,23,42,.05)",
                    }}
                >
                    <div className="flex flex-col min-w-0">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                color: "#64748B",
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: ".04em",
                                textTransform: "uppercase",
                            }}
                        >
                            <span>{currentPageMeta.section}</span>
                            <span style={{ color: "#CBD5E1" }}>•</span>
                            <span>Plataforma PM</span>
                        </div>

                        <div
                            style={{
                                color: "#0F172A",
                                fontSize: 18,
                                fontWeight: 950,
                                letterSpacing: "-.02em",
                                marginTop: 3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {currentPageMeta.title}
                        </div>

                        <div
                            style={{
                                color: "#64748B",
                                fontSize: 12,
                                fontWeight: 600,
                                marginTop: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {currentPageMeta.subtitle}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <div
                            className="hidden lg:flex items-center"
                            style={{
                                height: 38,
                                minWidth: 260,
                                borderRadius: 999,
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                padding: "0 14px",
                                color: "#94A3B8",
                                fontSize: 12,
                                fontWeight: 700,
                            }}
                        >
                            Buscar proyecto, oportunidad, tarea o contacto...
                        </div>

                        <AlertCenter />

                        <div
                            className="hidden sm:flex items-center gap-3"
                            style={{
                                borderLeft: "1px solid #E2E8F0",
                                paddingLeft: 14,
                            }}
                        >
                            <div className="text-right hidden md:block">
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 900,
                                        color: "#0F172A",
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {displayName}
                                </div>
                                <div
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        color: "#64748B",
                                        marginTop: 3,
                                    }}
                                >
                                    {roleLabel(role)}
                                </div>
                            </div>

                            <UserAvatar name={displayName} src={profilePhoto} size={38} />
                        </div>

                        <button
                            onClick={handleLogout}
                            style={{
                                height: 36,
                                padding: "0 14px",
                                borderRadius: 999,
                                background: "#0F172A",
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 900,
                                border: 0,
                                cursor: "pointer",
                                boxShadow: "0 12px 26px rgba(15,23,42,.18)",
                            }}
                        >
                            Salir
                        </button>
                    </div>
                </header>

                <main
                    className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto"
                    style={{
                        padding: 24,
                    }}
                >
                    {children}
                </main>
            </div>
        </div>
    );
}
